/**
 * The DOM layer the 3D view places each frame from View3DCore's overlay frame:
 * the object-name labels over the canvas, unless the host draws the labels
 * itself. It fills the canvas's parent and shows only while the view does.
 * (The drafting grid is drawn by View3DCore itself.)
 */

import type { LabelPlacement, OverlayFrame, SceneLabel } from '@kaiizen/kai-floor-plan-editor-core';
import { fBody, ink } from '../styles';

/**
 * The host draws the scene's labels itself: after each frame it gets the
 * scene's labels and each one's screen placement (aligned by index), and the
 * view draws none of its own.
 */
export type LabelsListener = (labels: readonly SceneLabel[], placements: readonly LabelPlacement[]) => void;

const LABEL_LAYER_STYLE = 'position:absolute;inset:0;pointer-events:none;overflow:hidden;display:none;';

const LABEL_STYLE = 'position:absolute;transform:translate(-50%,-50%);white-space:nowrap;visibility:hidden;'
  + `font-family:${fBody};font-weight:700;font-size:13px;color:${ink}`;

const createLayer = (className: string, cssText: string): HTMLDivElement => {
  const layer = document.createElement('div');
  layer.className = className;
  layer.style.cssText = cssText;
  return layer;
};

const createLabelElement = ({ text }: SceneLabel): HTMLSpanElement => {
  const labelElement = document.createElement('span');
  labelElement.textContent = text;
  labelElement.style.cssText = LABEL_STYLE;
  return labelElement;
};

/** Place a label at its projected anchor, hidden while the anchor is behind the camera. */
const placeLabel = (labelElement: HTMLElement, { visible, x, y }: LabelPlacement): void => {
  labelElement.style.visibility = visible ? 'visible' : 'hidden';
  if (!visible) return;
  labelElement.style.left = `${x}px`;
  labelElement.style.top = `${y}px`;
};

export class View3DOverlays {
  private labelLayer: HTMLDivElement | null;
  private onLabels?: LabelsListener;

  /** Mounts the label layer in the canvas's parent, over the canvas. */
  constructor(canvas: HTMLCanvasElement, onLabels?: LabelsListener) {
    const parent = canvas.parentElement;
    this.onLabels = onLabels;
    this.labelLayer = parent && !onLabels ? parent.appendChild(createLayer('view3d-labels', LABEL_LAYER_STYLE)) : null;
  }

  /** One <span> per label, rebuilt when the scene changes; place() moves them. */
  setLabels(labels: readonly SceneLabel[]): void {
    this.labelLayer?.replaceChildren(...labels.map(createLabelElement));
  }

  /** Lay the labels out for the frame just drawn, or hand them to the host that draws them. */
  place(frame: OverlayFrame, labels: readonly SceneLabel[]): void {
    const { labelLayer } = this;
    if (this.onLabels) this.onLabels(labels, frame.labels);
    else if (labelLayer) frame.labels.forEach((placement, index) => placeLabel(labelLayer.children[index] as HTMLElement, placement));
  }

  show(): void {
    this.setDisplay('');
  }

  hide(): void {
    this.setDisplay('none');
  }

  remove(): void {
    this.labelLayer?.remove();
    this.labelLayer = null;
  }

  private setDisplay(display: string): void {
    if (this.labelLayer) this.labelLayer.style.display = display;
  }
}
